'use client'

import { useEffect, useId, useRef, useState } from 'react'
import clsx from 'clsx'

import ChevronDownSmall from '/react4xp/components/prototype/icons/ChevronDownSmall'

import type { SelectButtonOption, SelectButtonProps } from './types'

const triggerClassName = 'group flex w-full items-center justify-between gap-2 rounded-xs border pl-3.5 pr-3 py-2 text-left transition-colors'
const panelClassName = 'absolute left-0 top-full z-20 flex w-full flex-col bg-black-950 transition-[opacity,transform] duration-200'
const itemClassName = 'flex w-full items-center px-3 py-2 text-sm leading-normal font-normal text-pure-white cursor-pointer'

export default function SelectButton({
	className,
	name,
	id,
	options,
	defaultValue,
	value,
	onChange,
	ariaLabel = 'Select an option',
	label,
}: SelectButtonProps) {
	const resolvedDefault = value || defaultValue || options[0] || { value: '', label: '' }

	const [selectedOption, setSelectedOption] = useState<SelectButtonOption>(resolvedDefault)
	const [isOpen, setIsOpen] = useState(false)
	const [activeIndex, setActiveIndex] = useState(0)
	const panelId = useId()
	const labelId = `${panelId}-label`
	const rootRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		if (value) setSelectedOption(value)
	}, [value])

	const selectedIndex = options.findIndex((option) => option.value === selectedOption?.value)

	function open(initialIndex: number) {
		setActiveIndex(initialIndex)
		setIsOpen(true)
	}

	function close() {
		setIsOpen(false)
		triggerRef.current?.focus()
	}

	function selectOption(index: number) {
		const option = options[index]
		if (!option) return
		setSelectedOption(option)
		close()
		onChange?.(option, { action: 'select-option', option, name })
	}

	function handleTriggerClick() {
		if (isOpen) {
			setIsOpen(false)
			return
		}
		open(selectedIndex >= 0 ? selectedIndex : 0)
	}

	function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault()
				if (!isOpen) open(selectedIndex >= 0 ? selectedIndex : 0)
				else setActiveIndex((index) => Math.min(index + 1, options.length - 1))
				break
			case 'ArrowUp':
				event.preventDefault()
				if (!isOpen) open(selectedIndex >= 0 ? selectedIndex : options.length - 1)
				else setActiveIndex((index) => Math.max(index - 1, 0))
				break
			case 'Home':
				if (isOpen) {
					event.preventDefault()
					setActiveIndex(0)
				}
				break
			case 'End':
				if (isOpen) {
					event.preventDefault()
					setActiveIndex(options.length - 1)
				}
				break
			case 'Enter':
			case ' ':
				event.preventDefault()
				if (!isOpen) open(selectedIndex >= 0 ? selectedIndex : 0)
				else selectOption(activeIndex)
				break
			case 'Escape':
				if (isOpen) {
					event.preventDefault()
					close()
				}
				break
			case 'Tab':
				if (isOpen) setIsOpen(false)
				break
		}
	}

	useEffect(() => {
		function handlePointerDown(event: MouseEvent) {
			if (isOpen && rootRef.current && !rootRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handlePointerDown)
		return () => document.removeEventListener('mousedown', handlePointerDown)
	}, [isOpen])

	if (options.length === 0) return null

	return (
		<div ref={rootRef} id={id} className={clsx('relative flex w-fit max-w-100 flex-col gap-2', className)}>
			{label && (
				<span id={labelId} className="body-small text-brand-300">
					{label}
				</span>
			)}

			<button
				ref={triggerRef}
				type="button"
				role="combobox"
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-controls={panelId}
				aria-labelledby={label ? labelId : undefined}
				aria-label={label ? undefined : ariaLabel}
				aria-activedescendant={isOpen ? `${panelId}-option-${activeIndex}` : undefined}
				onClick={handleTriggerClick}
				onKeyDown={handleKeyDown}
				className={clsx(
					triggerClassName,
					isOpen
						? 'border-brand-400 bg-brand-400'
						: 'border-brand-300 hover:border-brand-400 hover:bg-brand-400 focus-visible:border-brand-400 focus-visible:bg-brand-400',
				)}
			>
				<span className="relative grid min-w-0">
					{options.map((option) => (
						<span
							key={option.value}
							aria-hidden="true"
							className="invisible col-start-1 row-start-1 truncate text-sm leading-normal font-normal"
						>
							{option.label}
						</span>
					))}
					<span
						className={clsx(
							'col-start-1 row-start-1 truncate text-sm leading-normal font-normal',
							isOpen
								? 'text-brand-950'
								: 'text-pure-white group-hover:text-brand-950 group-focus-visible:text-brand-950',
						)}
					>
						{selectedOption?.label}
					</span>
				</span>
				<ChevronDownSmall
					className={clsx(
						'size-3 shrink-0 transition-transform',
						isOpen
							? 'rotate-180 text-brand-950'
							: 'text-pure-white group-hover:text-brand-950 group-focus-visible:text-brand-950',
					)}
					aria-hidden="true"
				/>
			</button>

			<ul
				id={panelId}
				role="listbox"
				aria-label={label ? undefined : ariaLabel}
				aria-labelledby={label ? labelId : undefined}
				{...(!isOpen ? ({ inert: '' } as Record<string, string>) : {})}
				className={clsx(panelClassName, isOpen ? 'visible opacity-100 translate-y-0' : 'invisible pointer-events-none opacity-0 -translate-y-1')}
			>
				{options.map((option, index) => {
					const isSelected = index === selectedIndex
					const isActive = isOpen && index === activeIndex
					return (
						<li key={option.value} role="presentation">
							<span
								id={`${panelId}-option-${index}`}
								role="option"
								aria-selected={isSelected}
								onMouseEnter={() => setActiveIndex(index)}
								onClick={() => selectOption(index)}
								className={clsx(itemClassName, (isSelected || isActive) && 'bg-brand-900', !isActive && 'hover:bg-brand-900')}
							>
								<span className="line-clamp-2 min-w-0">{option.label}</span>
							</span>
						</li>
					)
				})}
			</ul>

			{name && <input type="hidden" name={name} value={selectedOption?.value ?? ''} />}
		</div>
	)
}
